/* eslint-disable react-refresh/only-export-components */
import React from "react";
import { isToday, isYesterday, parseISO } from "date-fns";
import { getDateTimeByFormat } from "../../../../../redux/slices/dateTimeFormatSlice";
import { Avatar } from "../../../../../components/Avatar/Avatar";
import RightArrowIcon from "../../../../../assets/svg/right-arrow-icon";
import FlaggedIcon from "../../../../../assets/svg/flagged-icon";
import NoteIcon from "../../../../../assets/svg/note-icon";
import LogsIcon from "../../../../../assets/svg/logs-icon";
import SyncIcon from "../../../../../assets/svg/sync-icon";
import { TICKET_LOG_PRIORITY_STYLES } from "./constant";
import { getCategoryStyle } from "../components/AllTickets/utils/functions";
import type {
  HelpdeskActivityFieldUpdate,
  HelpdeskStage,
  HelpdeskTicketActivityLog,
  TicketCategoryOption,
  TicketLogsActivityTypeConfig,
} from "./types";

export type CampaignUserListItem = { status?: string };
import { getClientCRMFlag } from "../../../../../utils/constants/clientCustomCRMFlags";

export function filterActiveCampaignUsers<T extends CampaignUserListItem>(
  items: readonly T[] | undefined
): T[] {
  return (items ?? []).filter((u) => u.status === "active") as T[];
}

export function shouldShowHelpdeskViewSelector(
  clientCode: string,
  activeTab: string
): boolean {
  if (!isHelpdeskFeatureRequestsClient(clientCode)) {
    return true;
  }
  return activeTab !== "feature-requests";
}

const ISO_TIMESTAMP_REGEX =
  /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}/;

const TICKET_LOG_FIELD_KEYS_BY_ACTIVITY: Record<string, string[]> = {
  stage_updated: ["stage", "stage_key", "status"],
  priority_added: ["priority"],
  priority_updated: ["priority"],
  assignee_added: ["assignee", "assignees"],
  assignee_updated: ["assignee", "assignees"],
  assignee_removed: ["assignee", "assignees"],
  due_date_added: ["due_date"],
  due_date_updated: ["due_date"],
  tag_added: ["tag", "tags"],
  tag_removed: ["tag", "tags"],
  property_name_added: ["property_name", "property"],
  property_name_updated: ["property_name", "property"],
  ticket_category_added: ["ticket_category", "category"],
  ticket_category_updated: ["ticket_category", "category"],
  attachment_added: ["attachment", "file_name", "file", "s3_key"],
  attachment_removed: ["attachment", "file_name", "file", "s3_key"],
  comment_added: ["comment"],
  comment_reply: ["comment", "reply", "body"],
  comment_updated: ["comment"],
  comment_deleted: ["comment"],
  mention_added: ["tagged_user", "mention"],
  mention_removed: ["tagged_user", "mention"],
  custom_field_updated: [],
  description_added: ["description"],
  description_updated: ["description"],
  resolution_reason_added: ["resolution_reason"],
  resolution_reason_updated: ["resolution_reason"],
  external_vendor_added: ["external_vendor", "vendor"],
  external_vendor_updated: ["external_vendor", "vendor"],
  external_vendor_removed: ["external_vendor", "vendor"],
};

export const getActorName = (entry: HelpdeskTicketActivityLog): string => {
  if (typeof entry.created_by === "object" && entry.created_by?.name) {
    return entry.created_by.name;
  }
  return "System";
};

export const getFieldUpdate = (
  entry: HelpdeskTicketActivityLog,
  ...keys: string[]
): HelpdeskActivityFieldUpdate | undefined => {
  const list = entry.field_updates;
  if (!list?.length) return undefined;
  for (const key of keys) {
    const match = list.find(
      (field) => field.key_name.toLowerCase() === key.toLowerCase()
    );
    if (match) return match;
  }
  return list[0];
};

export const titleCase = (raw: string): string =>
  raw
    .replace(/[-_]/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

export const resolveStage = (
  stageKey: string,
  stages: HelpdeskStage[] | undefined
): { title: string; hex: string | undefined } => {
  const normalized = stageKey.trim().toLowerCase().replace(/[-_\s]+/g, " ");
  const stage = stages?.find(
    (s) =>
      s.key === stageKey ||
      s.key.toLowerCase() === stageKey.toLowerCase() ||
      s.title.toLowerCase().replace(/[-_\s]+/g, " ") === normalized
  );
  return {
    title: stage?.title ?? titleCase(stageKey),
    hex: stage?.secondary_hex_code || stage?.primary_hex_code || undefined,
  };
};

export const parsePriority = (
  raw: string
): { label: string; tier: "high" | "low" | "normal" } => {
  let value = raw.trim();
  const dotIndex = value.lastIndexOf(".");
  if (dotIndex !== -1) value = value.slice(dotIndex + 1);
  value = value.replace(/^priority[._]/i, "").replace(/_/g, " ").trim();

  const lower = value.toLowerCase();
  const label = lower === "medium" ? "Moderate" : titleCase(value) || raw;

  let tier: "high" | "low" | "normal" = "normal";
  if (/\b(high|urgent|critical|blocker)\b/.test(lower)) tier = "high";
  else if (/\b(low|minor|trivial)\b/.test(lower)) tier = "low";

  return { label, tier };
};

export const stripHtml = (html: string): string =>
  html
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

export const truncate = (text: string, max = 100): string => {
  const clean = stripHtml(text);
  return clean.length <= max ? clean : `${clean.slice(0, max)}…`;
};

export const formatFieldValue = (value: string, max = 32): string => {
  if (!value) return "";
  if (ISO_TIMESTAMP_REGEX.test(value.trim())) {
    const formatted = getDateTimeByFormat(parseISO(value.trim())).date;
    if (formatted) return formatted;
  }
  return truncate(value, max);
};

export const resolveUserName = (
  userId: string,
  usersMap: Map<string, string>
): string => usersMap.get(userId) || userId;

export const getFieldKeys = (activityType: string): string[] =>
  TICKET_LOG_FIELD_KEYS_BY_ACTIVITY[activityType] ?? [];

export const groupLogsByDate = (logs: HelpdeskTicketActivityLog[]) => {
  const today: HelpdeskTicketActivityLog[] = [];
  const yesterday: HelpdeskTicketActivityLog[] = [];
  const olderGroups: Record<string, HelpdeskTicketActivityLog[]> = {};

  for (const log of logs) {
    const date = parseISO(log.created_at);
    if (isToday(date)) {
      today.push(log);
    } else if (isYesterday(date)) {
      yesterday.push(log);
    } else {
      const dateKey = date.toISOString().split("T")[0];
      if (!olderGroups[dateKey]) olderGroups[dateKey] = [];
      olderGroups[dateKey].push(log);
    }
  }

  return { today, yesterday, olderGroups };
};

// --- Ticket logs activity UI ---

const ActorName = ({ name }: { name: string }) => (
  <span className="text-sm font-medium text-heading-text dark:text-heading-text-dk">
    {name}
  </span>
);

const GrayText = ({ children }: { children: React.ReactNode }) => (
  <span className="text-sm text-dark-gray dark:text-dark-gray-dk">
    {children}
  </span>
);

const AccentText = ({ children }: { children: React.ReactNode }) => (
  <span className="text-sm font-medium text-verba-indigo dark:text-verba-indigo-dk truncate">
    {children}
  </span>
);

const ValueText = ({ children }: { children: React.ReactNode }) => (
  <span className="text-sm font-medium text-heading-text dark:text-heading-text-dk">
    {children}
  </span>
);

const StageBadge = ({
  title,
  hex,
}: {
  title: string;
  hex: string | undefined;
}) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-md ${
      hex ? "" : "bg-verba-indigo dark:bg-verba-indigo-dk"
    }`}
    style={hex ? { backgroundColor: hex } : undefined}
  >
    <span className="text-sm font-semibold uppercase text-pure-white dark:text-pure-white">
      {title}
    </span>
  </span>
);

const capitalizeFirst = (text: string): string =>
  text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : "";

const resolveCategoryDisplayName = (
  rawValue: string,
  categories: TicketCategoryOption[] | undefined
): string => {
  if (!rawValue) return "";
  const matched = categories?.find(
    (cat) => cat?.key?.toLowerCase() === rawValue.toLowerCase()
  );
  if (matched?.display_name) return capitalizeFirst(matched.display_name);
  return titleCase(rawValue);
};

const CategoryChip = ({ displayName }: { displayName: string }) => (
  <span
    className={`inline-flex items-center text-sm font-medium px-2 py-0.5 rounded-md border ${getCategoryStyle(
      displayName
    )}`}
  >
    {displayName}
  </span>
);

const PriorityBadge = ({ raw }: { raw: string }) => {
  const { label, tier } = parsePriority(raw);
  const styles = TICKET_LOG_PRIORITY_STYLES[tier];
  return (
    <span className="inline-flex items-center gap-0.5">
      <FlaggedIcon className={`w-3 h-3 ${styles.flag}`} />
      <span className={`text-sm font-medium ${styles.text}`}>{label}</span>
    </span>
  );
};

const AssigneeBadge = ({
  value,
  usersMap,
}: {
  value: string;
  usersMap: Map<string, string>;
}) => {
  let displayName = value;
  try {
    const parsed = JSON.parse(value);
    if (parsed?.name) displayName = parsed.name;
    else if (parsed?._id) displayName = resolveUserName(parsed._id, usersMap);
    else if (parsed?.id) displayName = resolveUserName(parsed.id, usersMap);
  } catch {
    displayName = usersMap.get(value) || value;
  }
  return (
    <div className="inline-flex items-center gap-1">
      <Avatar text={displayName} size={18} className="text-[8px]" />
      <AccentText>{displayName}</AccentText>
    </div>
  );
};

const ICON_SYNC = (
  <SyncIcon className="w-3 h-3 text-verba-indigo dark:text-verba-indigo-dk" />
);
const ICON_NOTE = (
  <NoteIcon className="w-3 h-3 text-verba-indigo dark:text-verba-indigo-dk" />
);
const ICON_FLAG = (
  <FlaggedIcon className="w-3 h-3 text-verba-indigo dark:text-verba-indigo-dk" />
);
const ICON_FLAG_RED = (
  <FlaggedIcon className="w-3 h-3 text-verba-red-alert dark:text-verba-red-alert-dk" />
);
const ICON_LOG = (
  <LogsIcon className="w-3 h-3 text-verba-indigo dark:text-verba-indigo-dk" />
);

export const TICKET_LOG_ACTIVITY_CONFIG: Record<
  string,
  TicketLogsActivityTypeConfig
> = {
  ticket_created: {
    icon: ICON_LOG,
    content: (actor) => (
      <>
        <ActorName name={actor} /> <GrayText>created</GrayText>{" "}
        <ActorName name="Ticket" />
      </>
    ),
  },

  stage_updated: {
    icon: ICON_SYNC,
    content: (actor, fieldUpdate, { stages }) => {
      const oldStage = fieldUpdate?.old_value
        ? resolveStage(fieldUpdate.old_value, stages)
        : null;
      const newStage = fieldUpdate?.new_value
        ? resolveStage(fieldUpdate.new_value, stages)
        : null;
      return (
        <>
          <ActorName name={actor} />{" "}
          <GrayText>
            {oldStage ? "updated ticket status from" : "set ticket status to"}
          </GrayText>{" "}
          {oldStage && <StageBadge title={oldStage.title} hex={oldStage.hex} />}
          {oldStage && newStage && (
            <RightArrowIcon className="w-3.5 h-3.5 text-gray-placeholder dark:text-gray-placeholder-dk" />
          )}
          {newStage && <StageBadge title={newStage.title} hex={newStage.hex} />}
        </>
      );
    },
  },

  priority_added: {
    icon: ICON_FLAG,
    content: (actor, fieldUpdate) => (
      <>
        <ActorName name={actor} /> <GrayText>set priority to</GrayText>{" "}
        {fieldUpdate?.new_value && (
          <PriorityBadge raw={fieldUpdate.new_value} />
        )}
      </>
    ),
  },

  priority_updated: {
    icon: ICON_FLAG,
    content: (actor, fieldUpdate) => {
      const hasOld = !!fieldUpdate?.old_value;
      return (
        <div className="flex flex-col gap-1 min-w-0">
          <div className="flex flex-wrap items-center gap-x-[3px] gap-y-[2px]">
            <ActorName name={actor} />{" "}
            <GrayText>
              {hasOld ? "updated priority from" : "set priority to"}
            </GrayText>
          </div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {hasOld && <PriorityBadge raw={fieldUpdate!.old_value} />}
            {hasOld && fieldUpdate?.new_value && (
              <RightArrowIcon className="w-3.5 h-3.5 shrink-0 text-gray-placeholder dark:text-gray-placeholder-dk" />
            )}
            {fieldUpdate?.new_value && (
              <PriorityBadge raw={fieldUpdate.new_value} />
            )}
          </div>
        </div>
      );
    },
  },

  description_added: {
    icon: ICON_SYNC,
    content: (actor) => (
      <>
        <ActorName name={actor} />{" "}
        <GrayText>added the ticket description</GrayText>
      </>
    ),
  },

  description_updated: {
    icon: ICON_SYNC,
    content: (actor) => (
      <>
        <ActorName name={actor} />{" "}
        <GrayText>updated the ticket description</GrayText>
      </>
    ),
  },

  assignee_added: {
    icon: ICON_SYNC,
    content: (actor, fieldUpdate, { usersMap }) => (
      <>
        <ActorName name={actor} />{" "}
        <GrayText>assigned this ticket to</GrayText>{" "}
        {fieldUpdate?.new_value && (
          <AssigneeBadge value={fieldUpdate.new_value} usersMap={usersMap} />
        )}
      </>
    ),
  },

  assignee_updated: {
    icon: ICON_SYNC,
    content: (actor, fieldUpdate, { usersMap }) => (
      <>
        <ActorName name={actor} />{" "}
        <GrayText>reassigned this ticket to</GrayText>{" "}
        {fieldUpdate?.new_value && (
          <AssigneeBadge value={fieldUpdate.new_value} usersMap={usersMap} />
        )}
      </>
    ),
  },

  assignee_removed: {
    icon: ICON_SYNC,
    content: (actor, fieldUpdate, { usersMap }) => (
      <>
        <ActorName name={actor} />{" "}
        <span className="text-sm font-medium text-verba-red-alert dark:text-verba-red-alert-dk">
          removed
        </span>{" "}
        <GrayText>assignee from this ticket</GrayText>{" "}
        {fieldUpdate?.old_value && (
          <AssigneeBadge value={fieldUpdate.old_value} usersMap={usersMap} />
        )}
      </>
    ),
  },

  due_date_added: {
    icon: ICON_SYNC,
    content: (actor, fieldUpdate) => {
      const formatted = fieldUpdate?.new_value
        ? getDateTimeByFormat(parseISO(fieldUpdate.new_value)).date
        : null;
      return (
        <>
          <ActorName name={actor} />{" "}
          {formatted ? (
            <>
              <GrayText>set due date to</GrayText> <ValueText>{formatted}</ValueText>
            </>
          ) : (
            <GrayText>added due date</GrayText>
          )}
        </>
      );
    },
  },

  due_date_updated: {
    icon: ICON_SYNC,
    content: (actor, fieldUpdate) => {
      const from = fieldUpdate?.old_value
        ? getDateTimeByFormat(parseISO(fieldUpdate.old_value)).date
        : "";
      const to = fieldUpdate?.new_value
        ? getDateTimeByFormat(parseISO(fieldUpdate.new_value)).date
        : "";
      return (
        <>
          <ActorName name={actor} />{" "}
          {from && to ? (
            <>
              <GrayText>updated due date from</GrayText>{" "}
              <ValueText>{from}</ValueText> <GrayText>to</GrayText>{" "}
              <ValueText>{to}</ValueText>
            </>
          ) : to ? (
            <>
              <GrayText>set due date to</GrayText> <ValueText>{to}</ValueText>
            </>
          ) : (
            <GrayText>updated due date</GrayText>
          )}
        </>
      );
    },
  },

  overdue: {
    icon: ICON_FLAG_RED,
    content: () => (
      <>
        <ActorName name="Ticket" /> <GrayText>due date is</GrayText>{" "}
        <span className="text-sm font-medium text-verba-red-alert dark:text-verba-red-alert-dk">
          Overdue
        </span>
      </>
    ),
  },

  tag_added: {
    icon: ICON_SYNC,
    content: (actor, fieldUpdate) => {
      const tag = truncate(fieldUpdate?.new_value ?? "", 60);
      return (
        <>
          <ActorName name={actor} />{" "}
          {tag ? (
            <>
              <GrayText>added tag</GrayText> <ValueText>{tag}</ValueText>
            </>
          ) : (
            <GrayText>added a tag</GrayText>
          )}
        </>
      );
    },
  },

  tag_removed: {
    icon: ICON_SYNC,
    content: (actor, fieldUpdate) => {
      const tag = truncate(
        fieldUpdate?.old_value || fieldUpdate?.new_value || "",
        60
      );
      return (
        <>
          <ActorName name={actor} />{" "}
          {tag ? (
            <>
              <GrayText>removed tag</GrayText> <ValueText>{tag}</ValueText>
            </>
          ) : (
            <GrayText>removed a tag</GrayText>
          )}
        </>
      );
    },
  },

  property_name_added: {
    icon: ICON_SYNC,
    content: (actor, fieldUpdate) => {
      const val = truncate(fieldUpdate?.new_value ?? "", 60);
      return (
        <>
          <ActorName name={actor} />{" "}
          {val ? (
            <>
              <GrayText>set property name to</GrayText>{" "}
              <ValueText>{val}</ValueText>
            </>
          ) : (
            <GrayText>added property name</GrayText>
          )}
        </>
      );
    },
  },

  property_name_updated: {
    icon: ICON_SYNC,
    content: (actor, fieldUpdate) => {
      const from = truncate(fieldUpdate?.old_value ?? "", 40);
      const to = truncate(fieldUpdate?.new_value ?? "", 40);
      return (
        <>
          <ActorName name={actor} />{" "}
          {from && to ? (
            <>
              <GrayText>updated property name from</GrayText>{" "}
              <ValueText>{from}</ValueText> <GrayText>to</GrayText>{" "}
              <ValueText>{to}</ValueText>
            </>
          ) : to ? (
            <>
              <GrayText>set property name to</GrayText>{" "}
              <ValueText>{to}</ValueText>
            </>
          ) : (
            <GrayText>updated property name</GrayText>
          )}
        </>
      );
    },
  },

  ticket_category_added: {
    icon: ICON_SYNC,
    content: (actor, fieldUpdate, { ticketCategories }) => {
      const value = resolveCategoryDisplayName(
        fieldUpdate?.new_value ?? "",
        ticketCategories
      );
      return (
        <>
          <ActorName name={actor} />{" "}
          {value ? (
            <>
              <GrayText>set ticket category to</GrayText>{" "}
              <CategoryChip displayName={value} />
            </>
          ) : (
            <GrayText>added ticket category</GrayText>
          )}
        </>
      );
    },
  },

  ticket_category_updated: {
    icon: ICON_SYNC,
    content: (actor, fieldUpdate, { ticketCategories }) => {
      const from = resolveCategoryDisplayName(
        fieldUpdate?.old_value ?? "",
        ticketCategories
      );
      const to = resolveCategoryDisplayName(
        fieldUpdate?.new_value ?? "",
        ticketCategories
      );
      return (
        <>
          <ActorName name={actor} />{" "}
          <GrayText>
            {from ? "updated ticket category from" : "set ticket category to"}
          </GrayText>{" "}
          {from && <CategoryChip displayName={from} />}
          {from && to && (
            <RightArrowIcon className="w-3.5 h-3.5 text-gray-placeholder dark:text-gray-placeholder-dk" />
          )}
          {to && <CategoryChip displayName={to} />}
        </>
      );
    },
  },

  attachment_added: {
    icon: ICON_SYNC,
    content: (actor, fieldUpdate) => {
      const name = truncate(
        fieldUpdate?.new_value || fieldUpdate?.old_value || "file",
        80
      );
      return (
        <>
          <ActorName name={actor} /> <GrayText>added attachment</GrayText>{" "}
          <ValueText>{name}</ValueText>
        </>
      );
    },
  },

  attachment_removed: {
    icon: ICON_SYNC,
    content: (actor, fieldUpdate) => {
      const name = truncate(
        fieldUpdate?.old_value || fieldUpdate?.new_value || "file",
        80
      );
      return (
        <>
          <ActorName name={actor} /> <GrayText>removed attachment</GrayText>{" "}
          <ValueText>{name}</ValueText>
        </>
      );
    },
  },

  comment_added: {
    icon: ICON_NOTE,
    content: (actor, fieldUpdate) => (
      <>
        <ActorName name={actor} /> <GrayText>added a comment</GrayText>{" "}
        {fieldUpdate?.new_value && (
          <AccentText>
            &ldquo;{truncate(fieldUpdate.new_value, 120)}&rdquo;
          </AccentText>
        )}
      </>
    ),
  },

  comment_reply: {
    icon: ICON_NOTE,
    content: (actor, fieldUpdate) => (
      <>
        <ActorName name={actor} /> <GrayText>replied to a comment</GrayText>{" "}
        {fieldUpdate?.new_value && (
          <AccentText>
            &ldquo;{truncate(fieldUpdate.new_value, 120)}&rdquo;
          </AccentText>
        )}
      </>
    ),
  },

  comment_updated: {
    icon: ICON_NOTE,
    content: (actor, fieldUpdate) => (
      <>
        <ActorName name={actor} /> <GrayText>updated a comment</GrayText>{" "}
        {fieldUpdate?.new_value && (
          <AccentText>
            &ldquo;{truncate(fieldUpdate.new_value, 120)}&rdquo;
          </AccentText>
        )}
      </>
    ),
  },

  comment_deleted: {
    icon: ICON_NOTE,
    content: (actor, fieldUpdate) => (
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center flex-wrap gap-x-[3px] gap-y-[2px]">
          <ActorName name={actor} />{" "}
          <span className="text-sm font-medium text-verba-red-alert dark:text-verba-red-alert-dk">
            deleted
          </span>{" "}
          <GrayText>a comment</GrayText>
        </div>
        {(fieldUpdate?.new_value || fieldUpdate?.old_value) && (
          <AccentText>
            &ldquo;
            {truncate(
              fieldUpdate?.new_value || fieldUpdate?.old_value || "",
              200
            )}
            &rdquo;
          </AccentText>
        )}
      </div>
    ),
  },

  mention_added: {
    icon: ICON_NOTE,
    content: (actor, fieldUpdate, { usersMap }) => {
      const userId = fieldUpdate?.new_value || fieldUpdate?.old_value || "";
      const who = userId ? resolveUserName(userId, usersMap) : "";
      return (
        <>
          <ActorName name={actor} />{" "}
          {who ? (
            <>
              <GrayText>mentioned</GrayText> <ValueText>{who}</ValueText>{" "}
              <GrayText>in a comment</GrayText>
            </>
          ) : (
            <GrayText>mentioned someone in a comment</GrayText>
          )}
        </>
      );
    },
  },

  mention_removed: {
    icon: ICON_NOTE,
    content: (actor, fieldUpdate, { usersMap }) => {
      const userId = fieldUpdate?.old_value || fieldUpdate?.new_value || "";
      const who = userId ? resolveUserName(userId, usersMap) : "";
      return (
        <>
          <ActorName name={actor} />{" "}
          {who ? (
            <>
              <GrayText>removed mention of</GrayText>{" "}
              <ValueText>{who}</ValueText>
            </>
          ) : (
            <GrayText>removed a mention</GrayText>
          )}
        </>
      );
    },
  },

  resolution_reason_added: {
    icon: ICON_SYNC,
    content: (actor, fieldUpdate) => {
      const reason = truncate(
        fieldUpdate?.new_value || fieldUpdate?.old_value || "",
        60
      );
      return (
        <>
          <ActorName name={actor} />{" "}
          {reason ? (
            <>
              <GrayText>set resolution reason to</GrayText>{" "}
              <ValueText>{reason}</ValueText>
            </>
          ) : (
            <GrayText>added a resolution reason</GrayText>
          )}
        </>
      );
    },
  },

  resolution_reason_updated: {
    icon: ICON_SYNC,
    content: (actor, fieldUpdate) => {
      const from = truncate(fieldUpdate?.old_value ?? "", 60);
      const to = truncate(fieldUpdate?.new_value ?? "", 60);
      return (
        <>
          <ActorName name={actor} />{" "}
          {from && to ? (
            <>
              <GrayText>updated resolution reason from</GrayText>{" "}
              <ValueText>{from}</ValueText> <GrayText>to</GrayText>{" "}
              <ValueText>{to}</ValueText>
            </>
          ) : to ? (
            <>
              <GrayText>set resolution reason to</GrayText>{" "}
              <ValueText>{to}</ValueText>
            </>
          ) : (
            <GrayText>updated resolution reason</GrayText>
          )}
        </>
      );
    },
  },

  external_vendor_added: {
    icon: ICON_SYNC,
    content: (actor, fieldUpdate) => {
      const vendor = truncate(fieldUpdate?.new_value ?? "", 60);
      return (
        <>
          <ActorName name={actor} />{" "}
          {vendor ? (
            <>
              <GrayText>set external vendor to</GrayText>{" "}
              <ValueText>{vendor}</ValueText>
            </>
          ) : (
            <GrayText>added external vendor</GrayText>
          )}
        </>
      );
    },
  },

  external_vendor_updated: {
    icon: ICON_SYNC,
    content: (actor, fieldUpdate) => {
      const from = truncate(fieldUpdate?.old_value ?? "", 40);
      const to = truncate(fieldUpdate?.new_value ?? "", 40);
      return (
        <>
          <ActorName name={actor} />{" "}
          {from && to ? (
            <>
              <GrayText>updated external vendor from</GrayText>{" "}
              <ValueText>{from}</ValueText> <GrayText>to</GrayText>{" "}
              <ValueText>{to}</ValueText>
            </>
          ) : to ? (
            <>
              <GrayText>set external vendor to</GrayText>{" "}
              <ValueText>{to}</ValueText>
            </>
          ) : (
            <GrayText>updated external vendor</GrayText>
          )}
        </>
      );
    },
  },

  external_vendor_removed: {
    icon: ICON_SYNC,
    content: (actor, fieldUpdate) => {
      const vendor = truncate(
        fieldUpdate?.old_value || fieldUpdate?.new_value || "",
        60
      );
      return (
        <>
          <ActorName name={actor} />{" "}
          <span className="text-sm font-medium text-verba-red-alert dark:text-verba-red-alert-dk">
            removed
          </span>{" "}
          <GrayText>external vendor</GrayText>{" "}
          {vendor && <ValueText>{vendor}</ValueText>}
        </>
      );
    },
  },

  custom_field_updated: {
    icon: ICON_SYNC,
    content: (actor, fieldUpdate) => {
      const label = titleCase(fieldUpdate?.key_name ?? "custom field");
      const from = formatFieldValue(fieldUpdate?.old_value ?? "", 32);
      const to = formatFieldValue(fieldUpdate?.new_value ?? "", 32);
      return (
        <>
          <ActorName name={actor} />{" "}
          {from && to ? (
            <>
              <GrayText>{`updated ${label} from`}</GrayText>{" "}
              <ValueText>{from}</ValueText> <GrayText>to</GrayText>{" "}
              <ValueText>{to}</ValueText>
            </>
          ) : to ? (
            <>
              <GrayText>{`set ${label} to`}</GrayText>{" "}
              <ValueText>{to}</ValueText>
            </>
          ) : (
            <GrayText>{`updated ${label}`}</GrayText>
          )}
        </>
      );
    },
  },
};

export const TICKET_LOG_DEFAULT_ACTIVITY_CONFIG: TicketLogsActivityTypeConfig =
  {
    icon: ICON_LOG,
    content: (actor) => (
      <>
        <ActorName name={actor} /> <GrayText>updated ticket details</GrayText>
      </>
    ),
  };

export function isHelpdeskResolutionClient(clientCode: string): boolean {
  return getClientCRMFlag("hasHelpdeskResolution", clientCode);
}

export function isCancelledReasonModalClient(clientCode: string): boolean {
  return getClientCRMFlag("hasCancelledReasonModal", clientCode);
}

export function isHelpdeskFeatureRequestsClient(clientCode: string): boolean {
  return getClientCRMFlag("hasHelpdeskFeatureRequests", clientCode);
}

